import { useMemo, useState } from 'react';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Field,
  Input,
  Select,
  StatTile,
  Textarea,
  cmpDecimal,
  formatDate,
  formatMoney,
  formatQty,
  humanizeStatus,
  isDecimal,
  mulDecimal,
  roundDecimalString,
  subDecimal,
  sumDecimal,
} from '@biovet/ui';
import { ApiError, useResource } from '../lib/api.js';
import * as brokerage from '../lib/brokerage.js';
import type { AvailabilityRow, BrokerageRefs } from '../lib/brokerage.js';
import type { RecordSet } from '../lib/design-data.js';
import { RecordGrid } from './Records.js';
import { CurrencyRateFields } from './parts.js';
import { allCurrencies, baseCurrency } from '../lib/reference-data.js';

/**
 * The brokerage registers, reading and writing PostgreSQL.
 *
 * Part 1 of the business flow, and the only part of it that ever touches the
 * ledger is the commission invoice. Deal value, quantities and the buyer's
 * payments to the supplier are memorandum: they are selected out of `deals` and
 * `deal_settlements`, never out of `voucher_lines`, and the database refuses a
 * voucher sourced from `deals` at all.
 *
 * Each screen shapes the API's rows into the same `RecordSet` the sample screens
 * use, so the grid, the chips and the print layout stay in one place — but the
 * chips here actually filter, and each register carries the one write its own
 * users need: confirm a rate on the availability map, acknowledge a buyer
 * payment on pending settlement, take the commission in on the invoice register.
 *
 * There are four now, not five. The Import Deal Tracker went with the shipment
 * columns in `0021_simplify_deals.sql`.
 */

const R = 'right' as const;

/** Shared plumbing for a register that writes: busy, the last message, the last
 *  refusal. A refused write keeps the trigger's own sentence. */
function useAction(reload: () => void) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = async <T extends { message: string }>(work: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setNotice(null);
    setProblem(null);
    try {
      const result = await work();
      setNotice(result.message);
      reload();
      return result;
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.full : String(cause));
      return null;
    } finally {
      setBusy(false);
    }
  };

  return { busy, notice, problem, run };
}

function Messages({ notice, problem }: { notice: string | null; problem: string | null }) {
  if (!notice && !problem) return null;
  return (
    <div className="flex flex-col gap-2.5 pb-3.5" data-noprint="1">
      {problem && (
        <Callout tone="danger" title="That was refused">
          {problem}
        </Callout>
      )}
      {notice && (
        <Callout tone="success" title="Done">
          {notice}
        </Callout>
      )}
    </div>
  );
}

function Tiles({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4">{children}</div>;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Deal register ────────────────────────────────────────────────────────────

interface DealRow {
  dealNo: string;
  dealDate: string;
  kind: string;
  status: string;
  partyA: string;
  partyB: string;
  quantity: string | null;
  uom: string | null;
  currency: string;
  dealValue: string;
  dealValueBase: string;
  commissionBase: string;
  invoicedBase: string;
  receivedBase: string;
  balanceBase: string;
  accrualPolicy: string;
  commissionInvoiceNo: string | null;
}

const DEAL_FILTERS: Array<{ label: string; test: (d: DealRow) => boolean }> = [
  { label: 'All deals', test: () => true },
  { label: 'Import only', test: (d) => d.kind === 'IMPORT' },
  { label: 'Local only', test: (d) => d.kind === 'LOCAL' },
  {
    label: 'Open',
    test: (d) => !['CLOSED', 'CANCELLED', 'RECEIVED'].includes(d.status),
  },
  { label: 'Awaiting settlement', test: (d) => cmpDecimal(d.balanceBase, '0') > 0 },
  { label: 'Commission outstanding', test: (d) => cmpDecimal(d.invoicedBase, d.receivedBase) > 0 },
];

export function DealRegisterScreen({ onOpenDeal }: { onOpenDeal?: (dealNo: string) => void }) {
  const res = useResource<{ deals: DealRow[] }>('/deals');
  const all = useMemo(() => res.data?.deals ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const test = DEAL_FILTERS[chip]?.test ?? (() => true);
    const needle = search.trim().toLowerCase();
    return all.filter(
      (d) =>
        test(d) &&
        (needle === '' ||
          [d.dealNo, d.partyA, d.partyB].some((v) => v.toLowerCase().includes(needle))),
    );
  }, [all, chip, search]);

  const totals = useMemo(
    () => ({
      value: sumDecimal(rows.map((d) => d.dealValueBase)),
      commission: sumDecimal(rows.map((d) => d.commissionBase)),
      invoiced: sumDecimal(all.map((d) => d.invoicedBase)),
      received: sumDecimal(all.map((d) => d.receivedBase)),
      unsettled: sumDecimal(all.map((d) => d.balanceBase)),
    }),
    [rows, all],
  );

  const set: RecordSet = {
    crumb: 'Brokerage / Deal Register',
    title: 'Deal register',
    subtitle: 'Every brokered deal, with the commission it earns us.',
    chips: DEAL_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Deal no.', width: '150px', mono: true },
      { label: 'Date', width: '110px', mono: true },
      { label: 'Party A (supplier)' },
      { label: 'Party B (buyer)' },
      { label: 'Qty', align: R, mono: true, width: '110px' },
      { label: 'Deal value PKR', align: R, mono: true, width: '160px' },
      { label: 'Commission', align: R, mono: true, width: '140px' },
      { label: 'Invoiced', align: R, mono: true, width: '130px' },
      { label: 'Status', align: R, width: '130px', badge: true },
    ],
    rows: rows.map((d) => [
      d.dealNo,
      formatDate(d.dealDate),
      d.partyA,
      d.partyB,
      d.quantity ? `${formatQty(d.quantity, 0)} ${d.uom ?? ''}`.trim() : '—',
      formatMoney(d.dealValueBase, { decimals: 0 }),
      formatMoney(d.commissionBase, { decimals: 0 }),
      formatMoney(d.invoicedBase, { decimals: 0, dashOnZero: true }),
      humanizeStatus(d.status),
    ]),
    total: [
      '',
      '',
      `Memorandum deal value · ${rows.length} deal${rows.length === 1 ? '' : 's'}`,
      '',
      '',
      formatMoney(totals.value, { decimals: 0 }),
      formatMoney(totals.commission, { decimals: 0 }),
      formatMoney(totals.invoiced, { decimals: 0 }),
      '',
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      onRowSelect={onOpenDeal ? (i) => onOpenDeal(rows[i]?.dealNo ?? '') : undefined}
      actions={
        <>
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Deal no. or party"
            aria-label="Search deals"
            className="w-[200px]"
          />
        </>
      }
    >
      <Tiles>
        <Card>
          <StatTile label="Deals shown" value={String(rows.length)} hint={`${all.length} in all`} />
        </Card>
        <Card>
          <StatTile
            label="Commission earned"
            value={formatMoney(totals.commission, { decimals: 0 })}
            hint="gross, on shown deals"
          />
        </Card>
        <Card>
          <StatTile
            label="Commission outstanding"
            value={formatMoney(subDecimal(totals.invoiced, totals.received), { decimals: 0 })}
            hint="invoiced, not yet received"
          />
        </Card>
        <Card>
          <StatTile
            label="Buyer exposure"
            value={formatMoney(totals.unsettled, { decimals: 0 })}
            hint="memo — owed to suppliers, not to us"
          />
        </Card>
      </Tiles>
    </RecordGrid>
  );
}

// ── Product availability ─────────────────────────────────────────────────────

// `AvailabilityRow` moved to `lib/brokerage.ts`: the deal form reads the same
// list, and an API shape declared on one of the two screens that use it is a
// shape the other has to copy.

const AVAILABILITY_FILTERS: Array<{ label: string; test: (a: AvailabilityRow) => boolean }> = [
  { label: 'All', test: () => true },
  { label: 'Available', test: (a) => a.status === 'AVAILABLE' },
  { label: 'Limited', test: (a) => a.status === 'LIMITED' },
  { label: 'Out of season', test: (a) => a.status === 'OUT_OF_SEASON' },
  { label: 'Stale over 30 days', test: (a) => daysSince(a.lastConfirmed) > 30 },
];

interface AvailabilityDraft {
  id: string | null;
  productName: string;
  supplierCode: string;
  grade: string;
  originCountry: string;
  indicativeRate: string;
  currency: string;
  rateUomCode: string;
  status: string;
  portOfLoadingCode: string;
  incoterm: string;
  leadTimeDays: string;
  notes: string;
}

function blankAvailability(): AvailabilityDraft {
  return {
    id: null,
    productName: '',
    supplierCode: '',
    grade: '',
    originCountry: '',
    indicativeRate: '',
    currency: 'USD',
    rateUomCode: '',
    status: 'AVAILABLE',
    portOfLoadingCode: '',
    incoterm: '',
    leadTimeDays: '',
    notes: '',
  };
}

export function AvailabilityScreen() {
  const res = useResource<{ rows: AvailabilityRow[] }>('/product-availability');
  const refsRes = useResource<BrokerageRefs>('/brokerage-refs');
  const all = useMemo(() => res.data?.rows ?? [], [res.data]);
  const refs = refsRes.data;
  const { busy, notice, problem, run } = useAction(res.reload);

  const [chip, setChip] = useState(0);
  const [draft, setDraft] = useState<AvailabilityDraft | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  const rows = useMemo(
    () => all.filter(AVAILABILITY_FILTERS[chip]?.test ?? (() => true)),
    [all, chip],
  );

  const stale = all.filter((a) => daysSince(a.lastConfirmed) > 30).length;

  const set: RecordSet = {
    crumb: 'Brokerage / Product Availability',
    title: 'Product availability map',
    subtitle: 'What each supplier has to sell, and at what rate.',
    chips: AVAILABILITY_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Product / grade' },
      { label: 'Origin', width: '90px' },
      { label: 'Supplier (Party A)' },
      { label: 'Indicative rate', align: R, mono: true, width: '160px' },
      { label: 'Port of loading', width: '160px' },
      { label: 'Confirmed', align: R, mono: true, width: '120px' },
      { label: 'Status', align: R, width: '140px', badge: true },
    ],
    rows: rows.map((a) => [
      a.grade ? `${a.product} · ${a.grade}` : a.product,
      a.origin ?? '—',
      a.supplier,
      a.rate
        ? `${a.currency} ${formatMoney(a.rate, { decimals: 0 })} / ${a.rateUom ?? 'unit'}`
        : '—',
      a.portOfLoading ?? '—',
      freshness(a.lastConfirmed),
      humanizeStatus(a.status),
    ]),
  };

  const chosen = selected !== null ? rows[selected] : undefined;

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error ?? refsRes.error}
      chip={chip}
      onChipChange={(i) => {
        setChip(i);
        setSelected(null);
      }}
      onRowSelect={setSelected}
      selectedRow={selected}
      emptyTitle="Nothing on the map yet"
      actions={
        <>
          <Button
            size="sm"
            variant="primary"
            disabled={!refs}
            onClick={() => {
              setSelected(null);
              setDraft(blankAvailability());
            }}
          >
            Add product
          </Button>
        </>
      }
    >
      <Messages notice={notice} problem={problem} />

      <Tiles>
        <Card>
          <StatTile
            label="Lines on the map"
            value={String(all.length)}
            hint={`${rows.length} shown`}
          />
        </Card>
        <Card>
          <StatTile
            label="Suppliers"
            value={String(new Set(all.map((a) => a.supplierCode)).size)}
            hint="distinct Party A"
          />
        </Card>
        <Card>
          <StatTile
            label="Available now"
            value={String(all.filter((a) => a.status === 'AVAILABLE').length)}
            hint="ready to quote"
          />
        </Card>
        <Card>
          <StatTile label="Stale" value={String(stale)} hint="not confirmed in 30 days" />
        </Card>
      </Tiles>

      {chosen && !draft && (
        <Card flush data-noprint="1" className="mb-3.5">
          <CardHeader
            title={chosen.product}
            subtitle={`${chosen.supplier} · confirmed ${freshness(chosen.lastConfirmed).toLowerCase()}`}
            actions={
              <div className="flex gap-2">
                <Button
                  size="sm"
                  loading={busy}
                  onClick={() =>
                    run(() =>
                      brokerage
                        .saveAvailability({
                          id: chosen.id,
                          productName: chosen.product,
                          supplierCode: chosen.supplierCode,
                          grade: chosen.grade,
                          originCountry: chosen.origin,
                          indicativeRate: chosen.rate,
                          currency: chosen.currency,
                          rateUomCode: chosen.rateUomCode,
                          status: chosen.status,
                          portOfLoadingCode: chosen.portOfLoadingCode,
                          incoterm: chosen.incoterm,
                          leadTimeDays: chosen.leadTimeDays,
                          notes: chosen.notes,
                          confirmNow: true,
                        })
                        .then((r) => ({ ...r, message: `${chosen.product} re-confirmed today.` })),
                    )
                  }
                >
                  Re-confirm today
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    setDraft({
                      id: chosen.id,
                      productName: chosen.product,
                      supplierCode: chosen.supplierCode,
                      grade: chosen.grade ?? '',
                      originCountry: chosen.origin ?? '',
                      indicativeRate: chosen.rate ?? '',
                      currency: chosen.currency ?? 'USD',
                      rateUomCode: chosen.rateUomCode ?? '',
                      status: chosen.status,
                      portOfLoadingCode: chosen.portOfLoadingCode ?? '',
                      incoterm: chosen.incoterm ?? '',
                      leadTimeDays: chosen.leadTimeDays === null ? '' : String(chosen.leadTimeDays),
                      notes: chosen.notes ?? '',
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={busy}
                  onClick={async () => {
                    const ok = await run(() => brokerage.deleteAvailability(chosen.id));
                    if (ok) setSelected(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            }
          />
          {chosen.notes && (
            <CardBody>
              <p className="m-0 text-[12.5px] text-[var(--ink-2)]">{chosen.notes}</p>
            </CardBody>
          )}
        </Card>
      )}

      {draft && refs && (
        <Card flush data-noprint="1" className="mb-3.5">
          <CardHeader
            title={draft.id ? 'Edit availability' : 'Add to the availability map'}
            subtitle="Intel, not accounting — nothing here reaches the ledger"
          />
          <CardBody className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
            <Field label="Product" required className="md:col-span-2">
              <Input
                size="sm"
                value={draft.productName}
                onChange={(e) => setDraft({ ...draft, productName: e.target.value })}
                placeholder="Soybean meal 46%"
              />
            </Field>
            {/*
              Grade and origin are no longer asked for.

              They were two free-text boxes describing the product rather than
              the offer — and the offer is what this map is for: who has it, at
              what indicative rate, from which port, how fresh the confirmation
              is. A grade belongs in the product name an operator types
              ("Soybean meal 46%"), where it is one field rather than two that
              disagree, and origin is already implied by the port of loading.

              The **columns stay** and so do the draft fields: rows entered
              before this carry real values, `saveAvailability` still round-trips
              them, and an edit through this form must not blank a legacy row.
            */}
            <Field label="Supplier (Party A)" required className="md:col-span-2">
              <Select
                size="sm"
                value={draft.supplierCode}
                onChange={(e) => setDraft({ ...draft, supplierCode: e.target.value })}
              >
                <option value="">— choose —</option>
                {/* Party A only — the availability map is what a *brokered*
                    supplier has told us they can supply, and a feed supplier we
                    buy from is a different relationship. Maintained under
                    Brokerage → Deal Parties. */}
                {refs.parties
                  .filter((p) => p.isSeller)
                  .map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name} ({p.code})
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="Status">
              <Select
                size="sm"
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
              >
                {refs.enums.availabilityStatuses.map((s) => (
                  <option key={s} value={s}>
                    {humanizeStatus(s)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Lead time (days)">
              <Input
                size="sm"
                numeric
                value={draft.leadTimeDays}
                onChange={(e) => setDraft({ ...draft, leadTimeDays: e.target.value })}
              />
            </Field>

            <Field label="Indicative rate">
              <Input
                size="sm"
                numeric
                value={draft.indicativeRate}
                onChange={(e) => setDraft({ ...draft, indicativeRate: e.target.value })}
              />
            </Field>
            <Field label="Currency">
              <Select
                size="sm"
                value={draft.currency}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              >
                {currencyCodes().map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Per unit">
              <Select
                size="sm"
                value={draft.rateUomCode}
                onChange={(e) => setDraft({ ...draft, rateUomCode: e.target.value })}
              >
                <option value="">—</option>
                {refs.uoms.map((u) => (
                  <option key={u.code} value={u.code}>
                    {u.code}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Incoterm">
              <Select
                size="sm"
                value={draft.incoterm}
                onChange={(e) => setDraft({ ...draft, incoterm: e.target.value })}
              >
                <option value="">—</option>
                {refs.enums.incoterms.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Port of loading" className="md:col-span-2">
              <Select
                size="sm"
                value={draft.portOfLoadingCode}
                onChange={(e) => setDraft({ ...draft, portOfLoadingCode: e.target.value })}
              >
                <option value="">—</option>
                {refs.ports.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Notes" className="md:col-span-2">
              <Textarea
                rows={2}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </Field>

            <div className="flex gap-2 md:col-span-4">
              <Button
                size="sm"
                variant="primary"
                loading={busy}
                disabled={draft.productName.trim() === '' || draft.supplierCode === ''}
                onClick={async () => {
                  const ok = await run(() =>
                    brokerage.saveAvailability({
                      id: draft.id,
                      productName: draft.productName.trim(),
                      supplierCode: draft.supplierCode,
                      grade: draft.grade || null,
                      originCountry: draft.originCountry || null,
                      indicativeRate: isDecimal(draft.indicativeRate) ? draft.indicativeRate : null,
                      currency: draft.currency,
                      rateUomCode: draft.rateUomCode || null,
                      status: draft.status,
                      portOfLoadingCode: draft.portOfLoadingCode || null,
                      incoterm: draft.incoterm || null,
                      leadTimeDays: draft.leadTimeDays === '' ? null : Number(draft.leadTimeDays),
                      notes: draft.notes || null,
                      confirmNow: true,
                    }),
                  );
                  if (ok) setDraft(null);
                }}
              >
                {draft.id ? 'Save' : 'Add to the map'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </RecordGrid>
  );
}

// ── Commission invoices ──────────────────────────────────────────────────────

interface CommissionInvoiceRow {
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string | null;
  partyA: string;
  partyACode: string;
  dealNo: string | null;
  basis: string | null;
  rate: string | null;
  amountBase: string;
  whtPct: string;
  whtBase: string;
  receivedBase: string;
  outstandingBase: string;
  status: string;
  voucherNo: string | null;
}

const INVOICE_FILTERS: Array<{ label: string; test: (c: CommissionInvoiceRow) => boolean }> = [
  { label: 'All', test: () => true },
  { label: 'Outstanding', test: (c) => cmpDecimal(c.outstandingBase, '0') > 0 },
  { label: 'Received', test: (c) => cmpDecimal(c.receivedBase, '0') > 0 },
  { label: 'WHT deducted', test: (c) => cmpDecimal(c.whtBase, '0') > 0 },
  { label: 'Overdue', test: (c) => cmpDecimal(c.outstandingBase, '0') > 0 && isOverdue(c.dueDate) },
];

export function CommissionInvoiceScreen() {
  const res = useResource<{ rows: CommissionInvoiceRow[] }>('/commission-invoices');
  const banks = useResource<{ accounts: Array<{ code: string; name: string; isBank: boolean }> }>(
    '/accounts',
  );
  const all = useMemo(() => res.data?.rows ?? [], [res.data]);
  const { busy, notice, problem, run } = useAction(res.reload);

  const [chip, setChip] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [receipt, setReceipt] = useState({ receiptDate: today(), bankAccountCode: '' });

  const rows = useMemo(() => all.filter(INVOICE_FILTERS[chip]?.test ?? (() => true)), [all, chip]);
  const chosen = selected !== null ? rows[selected] : undefined;
  const bankOptions = (banks.data?.accounts ?? []).filter((a) => a.isBank);

  const totals = {
    amount: sumDecimal(rows.map((c) => c.amountBase)),
    wht: sumDecimal(rows.map((c) => c.whtBase)),
    outstanding: sumDecimal(rows.map((c) => c.outstandingBase)),
    received: sumDecimal(all.map((c) => c.receivedBase)),
  };

  const set: RecordSet = {
    crumb: 'Brokerage / Commission Invoice',
    title: 'Commission invoices on Party A',
    subtitle: 'Commission billed to suppliers, and what is still to be received.',
    chips: INVOICE_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Invoice no.', width: '160px', mono: true },
      { label: 'Date', width: '110px', mono: true },
      { label: 'Party A' },
      { label: 'Deal', width: '160px', mono: true },
      { label: 'Basis', width: '150px' },
      { label: 'Amount', align: R, mono: true, width: '140px' },
      { label: 'WHT', align: R, mono: true, width: '130px' },
      { label: 'Outstanding', align: R, mono: true, width: '140px' },
    ],
    rows: rows.map((c) => [
      c.invoiceNo,
      formatDate(c.invoiceDate),
      c.partyA,
      c.dealNo ?? '—',
      describeBasis(c.basis, c.rate),
      formatMoney(c.amountBase),
      formatMoney(c.whtBase, { dashOnZero: true }),
      formatMoney(c.outstandingBase, { dashOnZero: true }),
    ]),
    total: [
      '',
      '',
      'Commission receivable',
      '',
      '',
      formatMoney(totals.amount),
      formatMoney(totals.wht),
      formatMoney(totals.outstanding),
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={(i) => {
        setChip(i);
        setSelected(null);
      }}
      onRowSelect={setSelected}
      selectedRow={selected}
      emptyTitle="No commission has been invoiced yet"
    >
      <Messages notice={notice} problem={problem} />

      <Tiles>
        <Card>
          <StatTile
            label="Invoiced"
            value={formatMoney(totals.amount, { decimals: 0 })}
            hint={`${rows.length} invoice${rows.length === 1 ? '' : 's'}`}
          />
        </Card>
        <Card>
          <StatTile
            label="Received"
            value={formatMoney(totals.received, { decimals: 0 })}
            hint="gross, before withholding"
          />
        </Card>
        <Card>
          <StatTile
            label="Outstanding"
            value={formatMoney(totals.outstanding, { decimals: 0 })}
            hint="owed by Party A"
          />
        </Card>
        <Card>
          <StatTile
            label="Withheld at source"
            value={formatMoney(totals.wht, { decimals: 0 })}
            hint="a tax asset, recoverable"
          />
        </Card>
      </Tiles>

      {chosen && (
        <Card flush data-noprint="1" className="mb-3.5">
          <CardHeader
            title={`${chosen.invoiceNo} · ${chosen.partyA}`}
            subtitle={
              cmpDecimal(chosen.outstandingBase, '0') > 0
                ? `${formatMoney(chosen.outstandingBase)} outstanding${chosen.dueDate ? `, due ${formatDate(chosen.dueDate)}` : ''}`
                : 'Settled in full'
            }
          />
          {cmpDecimal(chosen.outstandingBase, '0') > 0 ? (
            <CardBody className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
              <Field label="Receipt date" required>
                <Input
                  size="sm"
                  type="date"
                  value={receipt.receiptDate}
                  onChange={(e) => setReceipt({ ...receipt, receiptDate: e.target.value })}
                />
              </Field>
              <Field label="Bank" className="md:col-span-2">
                <Select
                  size="sm"
                  value={receipt.bankAccountCode}
                  onChange={(e) => setReceipt({ ...receipt, bankAccountCode: e.target.value })}
                >
                  <option value="">— main PKR account —</option>
                  {bankOptions.map((a) => (
                    <option key={a.code} value={a.code}>
                      {a.code} · {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="flex items-end pb-1">
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  onClick={async () => {
                    const ok = await run(() =>
                      brokerage.recordCommissionReceipt(chosen.invoiceNo, {
                        receiptDate: receipt.receiptDate,
                        bankAccountCode: receipt.bankAccountCode || null,
                      }),
                    );
                    if (ok) setSelected(null);
                  }}
                >
                  Post receipt
                </Button>
              </div>
              <p className="m-0 text-[11.5px] text-[var(--ink-3)] md:col-span-4">
                Posts a BR voucher: {formatMoney(netOf(chosen))} to the bank,{' '}
                {formatMoney(whtOf(chosen))} to withholding tax receivable at{' '}
                {Number(chosen.whtPct)}
                %, and the receivable cleared in full.
              </p>
            </CardBody>
          ) : (
            <CardBody>
              <p className="m-0 text-[12.5px] text-[var(--ink-2)]">
                {formatMoney(chosen.receivedBase)} received
                {cmpDecimal(chosen.whtBase, '0') > 0
                  ? `, of which ${formatMoney(chosen.whtBase)} was withheld by Party A and sits in withholding tax receivable.`
                  : '.'}
                {chosen.voucherNo ? ` Raised as ${chosen.voucherNo}.` : ''}
              </p>
            </CardBody>
          )}
        </Card>
      )}
    </RecordGrid>
  );
}

/**
 * What Party A keeps back for the FBR, and what reaches the bank.
 *
 * The same arithmetic the API does in `numeric` — `round_money(outstanding ×
 * pct / 100)` — so the preview and the posted voucher agree to the paisa.
 */
function whtOf(c: CommissionInvoiceRow): string {
  if (!isDecimal(c.whtPct) || Number(c.whtPct) === 0) return '0';
  return roundDecimalString(mulDecimal(mulDecimal(c.outstandingBase, c.whtPct), '0.01'), 4);
}
function netOf(c: CommissionInvoiceRow): string {
  return subDecimal(c.outstandingBase, whtOf(c));
}

// ── Pending settlement ───────────────────────────────────────────────────────

interface SettlementRow {
  dealNo: string;
  partyB: string;
  deliveredAt: string | null;
  payableBase: string;
  confirmedBase: string;
  balanceBase: string;
  status: string;
  daysOutstanding: number | null;
}

const SETTLEMENT_FILTERS: Array<{ label: string; test: (s: SettlementRow) => boolean }> = [
  { label: 'All open', test: (s) => cmpDecimal(s.balanceBase, '0') > 0 },
  {
    label: 'Delivered, unpaid',
    test: (s) => s.deliveredAt !== null && cmpDecimal(s.confirmedBase, '0') === 0,
  },
  {
    label: 'Partially paid',
    test: (s) => cmpDecimal(s.confirmedBase, '0') > 0 && cmpDecimal(s.balanceBase, '0') > 0,
  },
  { label: 'Over 30 days', test: (s) => (s.daysOutstanding ?? 0) > 30 },
  { label: 'Settled', test: (s) => cmpDecimal(s.balanceBase, '0') === 0 },
  { label: 'Everything', test: () => true },
];

export function PendingSettlementScreen() {
  const res = useResource<{ rows: SettlementRow[] }>('/pending-settlement');
  const refsRes = useResource<BrokerageRefs>('/brokerage-refs');
  const all = useMemo(() => res.data?.rows ?? [], [res.data]);
  const { busy, notice, problem, run } = useAction(res.reload);

  const [chip, setChip] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const rows = useMemo(
    () => all.filter(SETTLEMENT_FILTERS[chip]?.test ?? (() => true)),
    [all, chip],
  );
  const chosen = selected !== null ? rows[selected] : undefined;

  const totals = {
    payable: sumDecimal(rows.map((s) => s.payableBase)),
    confirmed: sumDecimal(rows.map((s) => s.confirmedBase)),
    balance: sumDecimal(rows.map((s) => s.balanceBase)),
  };

  const set: RecordSet = {
    crumb: 'Brokerage / Pending Settlement',
    title: 'Pending settlement — Party B to Party A',
    subtitle: 'What each buyer still owes their supplier on a deal.',
    chips: SETTLEMENT_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Deal no.', width: '160px', mono: true },
      { label: 'Party B (buyer)' },
      { label: 'Delivered', width: '120px', mono: true },
      { label: 'Payable to A', align: R, mono: true, width: '160px' },
      { label: 'Confirmed paid', align: R, mono: true, width: '160px' },
      { label: 'Balance', align: R, mono: true, width: '160px' },
      { label: 'Ageing', align: R, width: '120px', badge: true },
    ],
    rows: rows.map((s) => [
      s.dealNo,
      s.partyB,
      s.deliveredAt ? formatDate(s.deliveredAt) : '—',
      formatMoney(s.payableBase, { decimals: 0 }),
      formatMoney(s.confirmedBase, { decimals: 0, dashOnZero: true }),
      formatMoney(s.balanceBase, { decimals: 0, dashOnZero: true }),
      ageing(s),
    ]),
    total: [
      '',
      'Unsettled buyer exposure (memo)',
      '',
      formatMoney(totals.payable, { decimals: 0 }),
      formatMoney(totals.confirmed, { decimals: 0 }),
      formatMoney(totals.balance, { decimals: 0 }),
      '',
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error ?? refsRes.error}
      chip={chip}
      onChipChange={(i) => {
        setChip(i);
        setSelected(null);
      }}
      onRowSelect={setSelected}
      selectedRow={selected}
      emptyTitle="Nothing outstanding"
    >
      <Messages notice={notice} problem={problem} />

      <Tiles>
        <Card>
          <StatTile
            label="Owed to suppliers"
            value={formatMoney(totals.balance, { decimals: 0 })}
            hint="memo — not our receivable"
          />
        </Card>
        <Card>
          <StatTile
            label="Acknowledged"
            value={formatMoney(totals.confirmed, { decimals: 0 })}
            hint="of the shown deals"
          />
        </Card>
        <Card>
          <StatTile
            label="Over 30 days"
            value={String(all.filter((s) => (s.daysOutstanding ?? 0) > 30).length)}
            hint="since delivery"
          />
        </Card>
        <Card>
          <StatTile
            label="Fully settled"
            value={String(all.filter((s) => cmpDecimal(s.balanceBase, '0') === 0).length)}
            hint="commission is earned"
          />
        </Card>
      </Tiles>

      {chosen && refsRes.data && cmpDecimal(chosen.balanceBase, '0') > 0 && (
        <SettlementCapture
          key={chosen.dealNo}
          row={chosen}
          refs={refsRes.data}
          busy={busy}
          onDone={() => setSelected(null)}
          run={run}
        />
      )}
    </RecordGrid>
  );
}

function SettlementCapture({
  row,
  refs,
  busy,
  onDone,
  run,
}: {
  row: SettlementRow;
  refs: BrokerageRefs;
  busy: boolean;
  onDone: () => void;
  run: <T extends { message: string }>(work: () => Promise<T>) => Promise<T | null>;
}) {
  const [form, setForm] = useState({
    settlementDate: today(),
    currency: baseCurrency(),
    fxRate: '1',
    amount: row.balanceBase,
    paymentMethod: 'BANK_TRANSFER',
    proofReference: '',
    confirmed: true,
  });

  return (
    <Card flush data-noprint="1" className="mb-3.5">
      <CardHeader
        title={`${row.dealNo} · ${row.partyB} paid Party A`}
        subtitle={`${formatMoney(row.balanceBase)} still owed. Recording this posts nothing — it releases commission.`}
      />
      <CardBody className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <Field label="Date" required>
          <Input
            size="sm"
            type="date"
            value={form.settlementDate}
            onChange={(e) => setForm({ ...form, settlementDate: e.target.value })}
          />
        </Field>
        <CurrencyRateFields
          currency={form.currency}
          fxRate={form.fxRate}
          onChange={(next) => setForm({ ...form, ...next })}
        />
        <Field label="Amount" required>
          <Input
            size="sm"
            numeric
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
        </Field>
        <Field label="Method">
          <Select
            size="sm"
            value={form.paymentMethod}
            onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
          >
            {refs.enums.paymentMethods.map((m) => (
              <option key={m} value={m}>
                {m.toLowerCase().replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Proof reference" className="md:col-span-2">
          <Input
            size="sm"
            value={form.proofReference}
            onChange={(e) => setForm({ ...form, proofReference: e.target.value })}
            placeholder="SWIFT ref, deposit slip, LC no."
          />
        </Field>
        <div className="flex items-end pb-1.5">
          <Checkbox
            checked={form.confirmed}
            onChange={(e) => setForm({ ...form, confirmed: e.target.checked })}
            label="Confirmed by Party A"
          />
        </div>
        <div className="flex gap-2 md:col-span-4">
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={!isDecimal(form.amount) || Number(form.amount) <= 0}
            onClick={async () => {
              const ok = await run(() =>
                brokerage.recordSettlement(row.dealNo, {
                  ...form,
                  proofReference: form.proofReference || null,
                }),
              );
              if (ok) onDone();
            }}
          >
            Record settlement
          </Button>
          <Button size="sm" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Import deal tracker — removed ────────────────────────────────────────────
//
// `0021_simplify_deals.sql` dropped the shipment columns this screen read. Where
// the goods are was never a fact about our books — they are Party B's — and the
// commission is timed by `deal_settlements`, which is what `accrual_policy`
// actually reads. Pending Settlement is where that now shows.

// ── Helpers ──────────────────────────────────────────────────────────────────

/** `PCT_OF_DEAL_VALUE` + `0.850000` → `0.85% of deal value`. */
function describeBasis(basis: string | null, rate: string | null): string {
  if (!basis) return '—';
  const trimmed = rate ? Number(rate).toString() : null;
  if (basis === 'PCT_OF_DEAL_VALUE')
    return trimmed ? `${trimmed}% of deal value` : '% of deal value';
  if (basis === 'PER_UNIT') return trimmed ? `${trimmed} per unit` : 'Per unit';
  if (basis === 'FLAT_AMOUNT') return 'Flat amount';
  return humanizeStatus(basis);
}

function daysSince(iso: string | null): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** `2 days ago`, `Today` — how stale a line of intel is. */
function freshness(iso: string | null): string {
  if (!iso) return 'Never';
  const days = daysSince(iso);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function isOverdue(dueDate: string | null): boolean {
  return dueDate !== null && new Date(dueDate) < new Date();
}

/**
 * The ageing badge.
 *
 * A settled deal says so; an open one says how long it has been open, which is
 * what `Records` colours on. A deal that has not been delivered yet is not late
 * — nothing is owed until the goods arrive.
 */
function ageing(row: SettlementRow): string {
  if (cmpDecimal(row.balanceBase, '0') === 0) return 'Settled';
  if (row.daysOutstanding === null) return 'Not delivered';
  return `${row.daysOutstanding} days`;
}

/**
 * The register's codes, for the plain pickers that carry no rate beside them.
 *
 * A product availability line names a currency and no rate — the rate belongs
 * to the deal that comes out of it, not to the offer.
 */
function currencyCodes(): string[] {
  const codes = allCurrencies().map((c) => c.code);
  return codes.length > 0 ? codes : [baseCurrency()];
}
